import React, { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { arrayRemove, arrayUnion, doc, deleteDoc, updateDoc, getDoc, setDoc, deleteField, writeBatch } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { app, db, API_BASE_URL } from '../../firebase/firebaseConfig';
import {
  DetailShell,
  HCard,
  HGhostButton,
  HPrimaryButton,
  HSecondaryButton,
  HDestructiveButton,
  HButtonPair,
  SerifHeading,
  HigloIcon,
  HearthIcon,
  SectionLabel,
  HBadge,
  HChip,
} from '../h';
import { useHiglos } from './useHiglos';
import { HigloRename } from './HigloRename';
import { HigloSettingsSection } from './HigloSettingsSection';
import { cn } from '../../lib/utils';

/* v9 5.2 / 5.5 / 5.6 — Higlo detail (single component, three states).

   5.2 — "internal only": user's Hearths are on positions, no external
         families. Shows the "Your Hearths" card + "Other families" empty
         area with a "+ Invite" ghost button.
   5.5 — pending invite: an external family sits in the pending list. We
         show it with an "Pending" warning chip + cancel X button.
   5.6 — accepted external family, local Hearth not on a position yet: the
         "not in this Higlo yet" row shows in dim text with a black "+ Add"
         pill in place of the PositionMini.

   Props (from the route):
     accountUUID, owningFamilies, primaryFamilyID — as usual from AppShell.
     iglooID — via route params.
*/
export function HigloDetail({
  accountUUID,
  owningFamilies,
  primaryFamilyID,
  iglooID: iglooIDProp,
  initialRenaming,
}) {
  const params = useParams();
  const navigate = useNavigate();
  const iglooID = iglooIDProp || params.iglooID;
  const higlos = useHiglos({ owningFamilies, accountUUID, primaryFamilyID });
  const higlo = higlos[iglooID];

  const [renaming, setRenaming] = useState(Boolean(initialRenaming));
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [addError, setAddError] = useState('');
  /* Optimistic "this external family is being removed" set — familyIDs
     whose rows should hide IMMEDIATELY on Cancel/Remove so the user
     doesn't see the transient "pending → member → gone" flicker that
     comes from multi-write Firestore snapshots landing one at a time. */
  const [hidingExternals, setHidingExternals] = useState(() => new Set());
  const markHidden = (fid) => {
    setHidingExternals((prev) => {
      const next = new Set(prev);
      next.add(fid);
      return next;
    });
  };
  const unmarkHidden = (fid) => {
    setHidingExternals((prev) => {
      if (!prev.has(fid)) return prev;
      const next = new Set(prev);
      next.delete(fid);
      return next;
    });
  };

  if (!higlo) {
    return (
      <DetailShell onBack={() => navigate('/app/higlos')} breadcrumb="Higlos">
        <div className="px-4 py-8 text-center">
          <p className="text-[13px] text-slate-500">Loading this Higlo…</p>
        </div>
      </DetailShell>
    );
  }

  const owned = higlo.ownedMemberships || [];
  // Actual in-Higlo rows — used for counts, editFamily, isOwner. Rows where
  // isMember===false are "+ Add" affordances, not real memberships.
  const ownedInIgloo = owned.filter((m) => m.isMember !== false);
  // Hide rows the user just cancelled/removed until Firestore catches up.
  const externalsRaw = higlo.externalFamilies || [];
  const externals = externalsRaw.filter((e) => !hidingExternals.has(e.familyID));
  const pendingExternals = externals.filter((e) => e.status === 'pending');
  const acceptedExternals = externals.filter((e) => e.status !== 'pending');

  // Total member Hearths across all memberships in this Higlo.
  const hearthCount = ownedInIgloo.length + acceptedExternals.length;
  // You + all accepted external families. Pending don't count.
  const totalFamilies = (ownedInIgloo.length > 0 ? 1 : 0) + acceptedExternals.length;

  const subtitleParts = [`Private family space`];
  subtitleParts.push(`${hearthCount} Hearth${hearthCount === 1 ? '' : 's'}`);
  if (pendingExternals.length > 0) {
    subtitleParts.push(`${pendingExternals.length} pending`);
  } else if (acceptedExternals.length > 0 && totalFamilies > 1) {
    subtitleParts[subtitleParts.length - 1] = `${hearthCount} Hearths, ${totalFamilies} families`;
  }
  const subtitle = subtitleParts.join(' · ');

  // The "editing" family for rename / delete writes — prefer primary (if it's
  // actually in the Higlo), fall back to first in-Higlo owned family.
  const editFamilyID = ownedInIgloo.some((m) => m.familyID === primaryFamilyID)
    ? primaryFamilyID
    : ownedInIgloo[0]?.familyID;

  // Any actual owned membership in this Higlo makes the user a deleter-eligible owner.
  const isOwner = ownedInIgloo.length > 0;

  // A caller is a "controller of this Higlo" if any of their owned families
  // sits in iglooInfo.controllers. Required for Delete, for evicting
  // external families, and for any backend-side write that checks caller auth.
  const controllers = higlo.controllers || [];
  const isCallerController = ownedInIgloo.some((m) => controllers.includes(m.familyID));
  // Preferred caller familyID for write endpoints — pick one that is actually
  // in controllers so /user/deleteIgloo and /user/removeMemberFromIgloo accept
  // the auth check. Falls back to editFamilyID otherwise.
  const controllerCallerID =
    ownedInIgloo.find((m) => controllers.includes(m.familyID))?.familyID || editFamilyID;

  // Delete gating. Backend /user/deleteIgloo refuses if:
  //   - caller is not a controller
  //   - the Higlo has any external families (pending OR accepted)
  //   - any family still has a position set
  // We mirror those rules in the UI so Delete only shows up when we can
  // actually deliver on it. The position sweep is handled inside handleDelete
  // because we own those families anyway.
  const hasExternalBlocker = externals.length > 0;
  const canDelete = isOwner && isCallerController && !hasExternalBlocker;

  // Delete the whole Higlo. Pre-gated by canDelete — by the time we get here
  // we already know the Higlo is owner-only and the caller is a controller.
  // We still need to clear any owned-Hearth positions first, because the
  // backend refuses to delete an "active" igloo. That cascade is safe because
  // every positioned Hearth belongs to the caller's own account.
  const handleDelete = async () => {
    if (!controllerCallerID) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const positioned = ownedInIgloo.filter((m) => m.position);
      await Promise.all(
        positioned.map((m) =>
          updateDoc(
            doc(db, 'familyInfo', m.familyID, 'igloos', iglooID),
            { position: deleteField() },
          ).catch((err) => {
            console.warn('clearing position failed', m.familyID, err);
          }),
        ),
      );

      const auth = getAuth(app);
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/user/deleteIgloo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ iglooID }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        setDeleteError(result.error || 'Failed to delete Higlo.');
        return;
      }
      setConfirmingDelete(false);
      navigate('/app/higlos');
    } catch (err) {
      console.error('HigloDetail delete error', err);
      setDeleteError('Failed to delete Higlo.');
    } finally {
      setDeleting(false);
    }
  };

  // Evict an accepted external family from this Higlo. Caller must be a
  // controller (already checked when surfacing the button). Refuses client-
  // side if the target is the only controller — backend has no such check
  // today, but we don't want to leave the Higlo with zero controllers.
  // Optimistic: hide the row immediately so the user doesn't see the
  // backend's multi-write settle frame-by-frame.
  const removeExternal = async (ext) => {
    if (!controllerCallerID) return;
    if (ext.isController && controllers.length === 1 && controllers[0] === ext.familyID) {
      return;
    }
    markHidden(ext.familyID);
    try {
      const auth = getAuth(app);
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/user/removeMemberFromIgloo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          iglooID,
          memberFamilyID: ext.familyID,
        }),
      });
      if (!res.ok) {
        // Endpoint refused — restore the row so the user can see/retry.
        unmarkHidden(ext.familyID);
      }
    } catch (err) {
      console.error('HigloDetail removeExternal error', err);
      unmarkHidden(ext.familyID);
    }
  };

  // Add one of the user's OTHER owned families to this Higlo as a
  // Controller (no position yet — Inactive). Mirrors OLD
  // ConfigureHiglos.handleAddToIgloo — same endpoint, same payload, no
  // client-side fallback. If the endpoint errors the UI surfaces it.
  const addOwnedFamily = async (fid) => {
    setAddError('');
    try {
      const auth = getAuth(app);
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/user/addOwnedFamilyToIgloo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ targetFamilyID: fid, iglooID }),
      });
      if (response.ok) return;
      const result = await response.json().catch(() => ({}));
      setAddError(result.error || 'Could not add this Hearth.');
    } catch (err) {
      console.error('HigloDetail addOwnedFamily error', err);
      setAddError('Could not add this Hearth.');
    }
  };

  /* Tear down pending + notifier + arrays for a pending invite. We batch
     the three writes with a single Firestore commit so listeners on both
     sides see a single snapshot update — otherwise there's a visible
     flicker where the pending deletion lands before the arrayRemove and
     the row briefly renders as "accepted Member". Optimistic local-hide
     covers the short delay before the batch commits. */
  const cancelInvite = async (fid) => {
    markHidden(fid);
    try {
      const batch = writeBatch(db);
      batch.delete(doc(db, 'iglooInfo', iglooID, 'pending', fid));
      batch.delete(doc(db, 'iglooInviteMessage', fid, 'iglooIDs', iglooID));
      batch.update(doc(db, 'iglooInfo', iglooID), {
        controllers: arrayRemove(fid),
        members: arrayRemove(fid),
      });
      await batch.commit();
    } catch (err) {
      console.error('HigloDetail cancelInvite error', err);
      unmarkHidden(fid);
    }
  };

  /* Rename an external family from MY side — writes a private nickname to
     `familyInfo/{controllerCallerID}/connectedFamilies/{externalFID}.name`
     with merge. Matches OLD IglooMembers.updateFamilyName. Other
     accounts are unaffected; this is only how I see them in my own
     dashboard. The name surfaces via useHiglos.connectedNames which
     already prefers this over the external family's own `familyInfo.name`. */
  const renameExternalFamily = async (ext, newName) => {
    const trimmed = (newName || '').trim();
    if (!trimmed || trimmed === ext.name) return;
    if (!controllerCallerID) return;
    try {
      await setDoc(
        doc(db, 'familyInfo', controllerCallerID, 'connectedFamilies', ext.familyID),
        { name: trimmed },
        { merge: true },
      );
    } catch (err) {
      console.error('renameExternalFamily error', err);
    }
  };

  // Promote / demote an accepted family. Mirrors the OLD
  // IglooMembers.changeMemberRole: direct mutation of iglooInfo.controllers
  // and .members. The only behavioural addition vs. OLD is the guard
  // against demoting the last controller — the OLD webapp didn't check
  // this, but orphaning a Higlo with zero controllers is strictly worse
  // than refusing the write, so we keep the guard.
  const toggleExternalRole = async (ext) => {
    try {
      const ref = doc(db, 'iglooInfo', iglooID);
      const snap = await getDoc(ref);
      if (!snap.exists()) return;
      const d = snap.data();
      const curControllers = d.controllers || [];
      const isController = curControllers.includes(ext.familyID);
      if (isController && curControllers.length <= 1) {
        console.warn('refusing to demote last controller', ext.familyID);
        return;
      }
      if (isController) {
        // Demote: remove from controllers, add to members.
        await updateDoc(ref, {
          controllers: arrayRemove(ext.familyID),
          members: arrayUnion(ext.familyID),
        });
      } else {
        // Promote: add to controllers, remove from members.
        await updateDoc(ref, {
          controllers: arrayUnion(ext.familyID),
          members: arrayRemove(ext.familyID),
        });
      }
    } catch (err) {
      console.error('HigloDetail toggleExternalRole error', err);
    }
  };

  return (
    <DetailShell
      onBack={() => navigate('/app/higlos')}
      breadcrumb="Higlos"
      rightSlot={
        editFamilyID && !renaming ? (
          <HGhostButton onClick={() => setRenaming(true)} className="text-[12px]">
            Rename
          </HGhostButton>
        ) : null
      }
    >
      <div className="px-4 pb-6">
        {/* Title block */}
        <div className="flex items-start gap-3 mb-2">
          <HigloIcon size="md" className="flex-shrink-0 mt-1" />
          <div className="flex-1 min-w-0">
            <HigloRename
              iglooID={iglooID}
              editFamilyID={editFamilyID}
              mode="account"
              targetFamilyIDs={ownedInIgloo.map((m) => m.familyID)}
              currentName={higlo.name}
              subtitle={!renaming ? subtitle : null}
              editing={renaming}
              onBegin={() => setRenaming(true)}
              onCancel={() => setRenaming(false)}
              onSaved={() => setRenaming(false)}
            />
          </div>
        </div>

        {/* Your Hearths — split into "in this Higlo" and "not in this Higlo
            yet" so the distinction is visually obvious. The "can add"
            section only renders when you have Hearths that aren't in the
            Higlo, and disappears once the last one is added. */}
        {(() => {
          const inHiglo = owned.filter((m) => m.isMember !== false);
          const addable = owned.filter((m) => m.isMember === false);
          return (
            <>
              {inHiglo.length > 0 && (
                <>
                  <SectionLabel className="mb-2 mt-4">
                    Your Hearths in this Higlo
                  </SectionLabel>
                  <HCard className="mb-4 overflow-hidden">
                    {inHiglo.map((m, idx) => (
                      <OwnedHearthRow
                        key={m.familyID}
                        membership={m}
                        divider={idx < inHiglo.length - 1}
                        onPositionPick={() =>
                          navigate(`/app/higlos/${iglooID}/position/${m.familyID}`)
                        }
                      />
                    ))}
                  </HCard>
                </>
              )}
              {addable.length > 0 && (
                <>
                  <SectionLabel className="mb-2 mt-4">
                    Add your other Hearths
                  </SectionLabel>
                  <HCard className="mb-4 overflow-hidden">
                    {addable.map((m, idx) => (
                      <AddHearthRow
                        key={m.familyID}
                        membership={m}
                        divider={idx < addable.length - 1}
                        onAdd={() => addOwnedFamily(m.familyID)}
                      />
                    ))}
                  </HCard>
                </>
              )}
              {addError && (
                <p className="text-[12px] text-red-600 leading-relaxed mb-3">
                  {addError}
                </p>
              )}
            </>
          );
        })()}

        {/* Other families header. Only a controller may invite — the security
            rules reject an invite from a member, so offering the button to one
            leads to a dead end. */}
        <div className="flex items-end justify-between mb-2">
          <SectionLabel>Other families</SectionLabel>
          {isCallerController && (
            <HGhostButton
              onClick={() => navigate(`/app/higlos/${iglooID}/invite`)}
              className="text-[12px] py-1 px-2"
            >
              + Invite
            </HGhostButton>
          )}
        </div>

        {externals.length === 0 ? (
          <p className="text-[12px] text-slate-500 leading-relaxed mb-4">
            You can invite families outside the Hearths you manage to this Higlo,
            using their email.
          </p>
        ) : (
          <HCard className="mb-4 overflow-hidden">
            {externals.map((ext, idx) => (
              <ExternalFamilyRow
                key={ext.familyID}
                ext={ext}
                divider={idx < externals.length - 1}
                canRemove={isCallerController && ext.status !== 'pending'}
                canToggleRole={isCallerController && ext.status !== 'pending'}
                canRename={!!controllerCallerID && ext.status !== 'pending'}
                onCancel={() => cancelInvite(ext.familyID)}
                onRemove={() => removeExternal(ext)}
                onToggleRole={() => toggleExternalRole(ext)}
                onRename={(newName) => renameExternalFamily(ext, newName)}
              />
            ))}
          </HCard>
        )}

        {/* Higlo-level settings — controllers only (the rules reject writes
            from anyone else). The section discovers its settings from the
            controller-visible catalog at runtime and hides itself entirely
            while loading, on any load failure, or when no controller-
            controllable setting is deployed. */}
        {isCallerController && <HigloSettingsSection iglooID={iglooID} />}

        {/* Delete — only offered to controllers, only when the Higlo has no
            external families. If externals are present the backend would
            reject and it's on the user to evict them first. */}
        {isOwner && isCallerController && (
          <>
            <HSecondaryButton
              onClick={() => {
                if (!canDelete) return;
                setDeleteError('');
                setConfirmingDelete(true);
              }}
              disabled={!canDelete}
              className="w-full text-[13px] text-slate-500"
              style={{ paddingTop: '10px', paddingBottom: '10px' }}
            >
              Delete this Higlo
            </HSecondaryButton>
            {hasExternalBlocker && (
              <p className="text-[11px] text-slate-500 leading-relaxed text-center mt-2">
                Remove the other families first — a Higlo can only be deleted
                when it&rsquo;s just your Hearths left.
              </p>
            )}
          </>
        )}
      </div>

      {confirmingDelete && (
        <DeleteHigloDialog
          higloName={higlo.name}
          onCancel={() => {
            if (deleting) return;
            setConfirmingDelete(false);
            setDeleteError('');
          }}
          onConfirm={handleDelete}
          isWorking={deleting}
          errorMessage={deleteError}
        />
      )}
    </DetailShell>
  );
}

/* Destructive confirmation modal before wiping a Higlo. Mirrors the
   FactoryResetDialog pattern (dimmed backdrop, centered card, Cancel +
   red confirm). */
function DeleteHigloDialog({ higloName, onCancel, onConfirm, isWorking, errorMessage }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-xl ring-1 ring-slate-200 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 rounded-full flex items-center justify-center bg-red-50">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#dc2626"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
        </div>
        <SerifHeading size="lg" className="text-center mb-3">
          Delete <span className="italic">{higloName || 'this Higlo'}</span>?
        </SerifHeading>
        <p className="text-[13px] text-slate-600 leading-relaxed mb-4 text-center max-w-[320px] mx-auto">
          This removes it from every family in it. This can&rsquo;t be undone.
        </p>
        {errorMessage && (
          <p className="text-[12px] text-red-600 text-center mb-3">{errorMessage}</p>
        )}
        <HButtonPair>
          <HSecondaryButton onClick={onCancel} disabled={isWorking}>
            Cancel
          </HSecondaryButton>
          <HDestructiveButton onClick={onConfirm} disabled={isWorking}>
            {isWorking ? 'Deleting…' : 'Delete'}
          </HDestructiveButton>
        </HButtonPair>
      </div>
    </div>
  );
}

/* ───────── subcomponents ───────── */

function OwnedHearthRow({ membership, divider, onPositionPick }) {
  const { familyName, position } = membership;
  // In-Higlo but no position yet — tapping navigates to the position picker.
  if (!position) {
    return (
      <button
        type="button"
        onClick={onPositionPick}
        className={cn(
          'w-full px-4 py-3 flex items-center gap-3 text-left transition-colors',
          'hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50',
          divider && 'border-b border-slate-100',
        )}
      >
        <HearthIcon size="sm" className="flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-semibold text-slate-900 truncate">
            {familyName || 'Hearth'}
          </div>
          <div className="text-[11px] text-slate-500">Tap to pick a position</div>
        </div>
        <PositionMini position={null} />
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onPositionPick}
      className={cn(
        'w-full px-4 py-3 flex items-center gap-3 text-left transition-colors',
        'hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50',
        divider && 'border-b border-slate-100',
      )}
    >
      <HearthIcon size="sm" className="flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold text-slate-900 truncate">
          {familyName || 'Hearth'}
        </div>
        <div className="text-[11px] text-slate-500">
          Shows on the <strong>{positionWord(position)}</strong>
        </div>
      </div>
      <PositionMini position={position} />
    </button>
  );
}

/* Thin row used in the "Add your other Hearths" section below the
   in-Higlo list. No icon, smaller height, just the Hearth name and a
   "+ Add" affordance — deliberately visually different from the OwnedHearthRow
   so members and non-members don't blur together. */
function AddHearthRow({ membership, divider, onAdd }) {
  return (
    <button
      type="button"
      onClick={onAdd}
      className={cn(
        'w-full px-4 py-2.5 flex items-center justify-between gap-3 text-left transition-colors',
        'hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50',
        divider && 'border-b border-slate-100',
      )}
    >
      <span className="text-[13px] text-slate-700 truncate">
        {membership.familyName || 'Hearth'}
      </span>
      <span className="inline-flex items-center gap-1 text-[12px] font-medium text-slate-900 flex-shrink-0">
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        Add
      </span>
    </button>
  );
}

function ExternalFamilyRow({
  ext,
  divider,
  canRemove,
  canToggleRole,
  canRename,
  onCancel,
  onRemove,
  onToggleRole,
  onRename,
}) {
  const isPending = ext.status === 'pending';
  const initial = (ext.name || 'F').trim().charAt(0).toUpperCase();
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [confirmingRole, setConfirmingRole] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState('');
  const nameInputRef = useRef(null);

  const startRename = () => {
    setDraftName(ext.name || '');
    setEditingName(true);
    setTimeout(() => nameInputRef.current?.focus(), 0);
  };
  const cancelNameEdit = () => {
    setEditingName(false);
    setDraftName('');
  };
  const commitNameEdit = () => {
    const trimmed = (draftName || '').trim();
    if (trimmed && trimmed !== ext.name) {
      onRename?.(trimmed);
    }
    cancelNameEdit();
  };

  let subline;
  if (isPending) {
    const relative = formatInvitedRelative(ext.invitedAt);
    subline = relative
      ? `Invited ${relative} · waiting for their reply.`
      : 'Waiting for their reply';
  } else if (ext.position) {
    subline = (
      <>
        Shows on the <strong>{positionWord(ext.position)}</strong>
      </>
    );
  } else {
    subline = 'In this Higlo';
  }

  return (
    <div
      className={cn(
        'px-4 py-3 flex items-center gap-3',
        divider && 'border-b border-slate-100',
        isPending && 'opacity-80',
      )}
    >
      <div
        className={cn(
          'w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-[13px] font-semibold bg-slate-100 text-slate-900',
        )}
        style={isPending ? { background: '#f8fafc' } : undefined}
      >
        {initial}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          {editingName ? (
            <input
              ref={nameInputRef}
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              onBlur={commitNameEdit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitNameEdit();
                if (e.key === 'Escape') cancelNameEdit();
              }}
              className="text-[14px] font-semibold text-slate-900 bg-transparent outline-none border-b border-slate-300 focus:border-slate-900 min-w-0 flex-1"
            />
          ) : canRename && !isPending ? (
            <button
              type="button"
              onClick={startRename}
              className={cn(
                'text-[14px] font-semibold truncate text-left',
                isPending ? 'text-slate-700' : 'text-slate-900',
                'hover:underline decoration-dotted underline-offset-2',
              )}
              aria-label={`Rename ${ext.name}`}
              title="Tap to rename on your dashboard"
            >
              {ext.name}
            </button>
          ) : (
            <div
              className={cn(
                'text-[14px] font-semibold truncate',
                isPending ? 'text-slate-700' : 'text-slate-900',
              )}
            >
              {ext.name}
            </div>
          )}
          {isPending ? (
            <HChip className="bg-amber-100 text-amber-700 border-amber-200">
              Pending
            </HChip>
          ) : canToggleRole ? (
            <button
              type="button"
              onClick={() => setConfirmingRole(true)}
              className="inline-flex items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
              aria-label={
                ext.isController ? 'Demote to member' : 'Make Controller'
              }
            >
              {ext.isController ? (
                <HBadge variant="controller">Controller</HBadge>
              ) : (
                <HChip className="bg-slate-100 text-slate-600 border-slate-200">
                  Member
                </HChip>
              )}
            </button>
          ) : ext.isController ? (
            <HBadge variant="controller">Controller</HBadge>
          ) : null}
        </div>
        <div className="text-[11px] text-slate-500 truncate">{subline}</div>
      </div>
      {!isPending && ext.position && (
        <PositionMini position={ext.position} />
      )}
      {isPending && (
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel invite"
          className="text-[16px] text-slate-500 hover:text-slate-700 flex-shrink-0 px-2"
        >
          ×
        </button>
      )}
      {!isPending && canRemove && (
        <button
          type="button"
          onClick={() => setConfirmingRemove(true)}
          aria-label={`Remove ${ext.name} from this Higlo`}
          className="ml-1 flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6" />
            <path d="M14 11v6" />
          </svg>
        </button>
      )}
      {confirmingRemove && (
        <RemoveExternalDialog
          name={ext.name}
          onCancel={() => setConfirmingRemove(false)}
          onConfirm={() => {
            setConfirmingRemove(false);
            onRemove?.();
          }}
        />
      )}
      {confirmingRole && (
        <RoleChangeDialog
          name={ext.name}
          currentlyController={ext.isController}
          onCancel={() => setConfirmingRole(false)}
          onConfirm={() => {
            setConfirmingRole(false);
            onToggleRole?.();
          }}
        />
      )}
    </div>
  );
}

function RoleChangeDialog({ name, currentlyController, onCancel, onConfirm }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-xl ring-1 ring-slate-200 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <SerifHeading size="md" className="text-center mb-2">
          {currentlyController
            ? `Make ${name || 'this family'} a Member?`
            : `Make ${name || 'this family'} a Controller?`}
        </SerifHeading>
        <p className="text-[13px] text-slate-600 leading-relaxed mb-4 text-center max-w-[320px] mx-auto">
          {currentlyController
            ? 'They\u2019ll no longer be able to invite other families, change positions, or rename this Higlo.'
            : 'Controllers can invite other families, change positions, rename this Higlo, and remove members.'}
        </p>
        <HButtonPair>
          <HSecondaryButton onClick={onCancel}>Cancel</HSecondaryButton>
          <HPrimaryButton onClick={onConfirm}>
            {currentlyController ? 'Make Member' : 'Make Controller'}
          </HPrimaryButton>
        </HButtonPair>
      </div>
    </div>
  );
}

function RemoveExternalDialog({ name, onCancel, onConfirm }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-xl ring-1 ring-slate-200 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <SerifHeading size="md" className="text-center mb-2">
          Remove {name || 'this family'}?
        </SerifHeading>
        <p className="text-[13px] text-slate-600 leading-relaxed mb-4 text-center max-w-[320px] mx-auto">
          They won&rsquo;t see this Higlo on their Hearth any more. You can
          invite them back later.
        </p>
        <HButtonPair>
          <HSecondaryButton onClick={onCancel}>Cancel</HSecondaryButton>
          <HDestructiveButton onClick={onConfirm}>Remove</HDestructiveButton>
        </HButtonPair>
      </div>
    </div>
  );
}

/* Compact relative-time formatter for the "Invited X ago" prefix.
   Accepts a JS Date, a Firestore Timestamp-like object, or epoch ms/seconds.
   Returns null if we can't read a usable time. */
function formatInvitedRelative(invitedAt) {
  if (invitedAt == null) return null;
  let ms = null;
  if (invitedAt instanceof Date) {
    ms = invitedAt.getTime();
  } else if (typeof invitedAt === 'number') {
    ms = invitedAt < 1e12 ? invitedAt * 1000 : invitedAt;
  } else if (typeof invitedAt?.toMillis === 'function') {
    try { ms = invitedAt.toMillis(); } catch (_) { ms = null; }
  } else if (typeof invitedAt?.seconds === 'number') {
    ms = invitedAt.seconds * 1000;
  }
  if (!ms) return null;
  const diffSec = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return 'yesterday';
  if (diffDay < 7) return `${diffDay} days ago`;
  const diffWk = Math.floor(diffDay / 7);
  if (diffWk < 5) return `${diffWk} week${diffWk === 1 ? '' : 's'} ago`;
  const diffMo = Math.floor(diffDay / 30);
  return `${diffMo} month${diffMo === 1 ? '' : 's'} ago`;
}

function PositionMini({ position }) {
  const slots = ['Left', 'Centre', 'Right'];
  return (
    <div
      className="inline-flex items-center gap-1 px-1.5 py-1 rounded border border-slate-200 bg-slate-50 flex-shrink-0"
      aria-label={position ? `Position ${position}` : 'No position'}
    >
      {slots.map((s) => (
        <span
          key={s}
          className={cn(
            'w-[5px] h-[5px] rounded-full',
            position === s ? 'bg-slate-900' : 'bg-slate-200',
          )}
        />
      ))}
    </div>
  );
}

function positionWord(p) {
  return p ? p.toLowerCase() : '';
}
